/**
 * @fileoverview Hook de Actions Layer para manejar acciones de llamada Fonema (contestar y declinar).
 * @module fonema/actions/useFonemaCallActions
 * @category Hook - Actions Layer
 * @since 1.0.0
 * @version 1.0.0
 */
import { useCallback, useRef } from 'react';
import createFonemaCallEvent from '../utils/fonemaCallEventService';

/**
 * Hook for Fonema call actions (answer and decline).
 */
export default function useFonemaCallActions({
  disableButtons,
  callInfoData,
  enabled = true
}) {
  const callInfoDataRef = useRef(callInfoData);
  callInfoDataRef.current = callInfoData;

  const noop = useCallback(async () => {}, []);
  const noopBool = useCallback(() => false, []);

  const answerCall = useCallback(async () => {
    if (!enabled) return;
    if (window.FonemaWhatsappAudio?.answerCall && callInfoDataRef.current?.callId) {
      disableButtons();
      await window.FonemaWhatsappAudio.answerCall(callInfoDataRef.current.callId);
    }
  }, [disableButtons, enabled]);

  const cancelCall = useCallback(async () => {
    if (!enabled) return;
    if (window.FonemaWhatsappAudio?.cancelCall) {
      disableButtons();
      createFonemaCallEvent({
        callData: callInfoDataRef.current,
        customerId: callInfoDataRef.current?.customer?.id,
        endedReason: 'agent-cancelled-call',
        status: 'declined'
      });
      await window.FonemaWhatsappAudio.cancelCall();
    }

    // Clean up all references using the global function
    if (window.clearFonemaCallReferences) {
      window.clearFonemaCallReferences();
    }
  }, [disableButtons, enabled]);

  const declineCall = useCallback(async () => {
    if (!enabled) return;
    if (window.FonemaWhatsappAudio?.declineCall && callInfoDataRef.current?.callId) {
      disableButtons();
      createFonemaCallEvent({
        callData: callInfoDataRef.current,
        customerId: callInfoDataRef.current?.customer?.id,
        endedReason: 'agent-declined-call',
        status: 'declined'
      });
      await window.FonemaWhatsappAudio.declineCall(callInfoDataRef.current.callId);
    }
  }, [disableButtons, enabled]);

  const endCall = useCallback(async () => {
    if (!enabled) return;
    if (window.FonemaWhatsappAudio?.endCall && callInfoDataRef.current?.callId) {
      disableButtons();
      await window.FonemaWhatsappAudio.endCall(callInfoDataRef.current.callId);
    }

    // Clean up all references using the global function
    if (window.clearFonemaCallReferences) {
      window.clearFonemaCallReferences();
    }
  }, [disableButtons, enabled]);

  const toggleMic = useCallback(() => {
    if (!enabled) return false;
    if (window.FonemaWhatsappAudio?.toggleMic) {
      window.FonemaWhatsappAudio.toggleMic();
      const state = window.FonemaWhatsappAudio.getCallState();
      return state.isMuted;
    }
    return false;
  }, [enabled]);

  const call = useCallback(async ({ to }) => {
    if (!enabled) return false;
    // Normalize the numbers to ensure E.164 format with only one + and only digits
    const normalize = (num) => {
      if (!num) return '';
      // Remove all non-numeric characters except the +
      let n = num.trim().replace(/[^\d+]/g, '');
      // Remove all + except the first one
      n = n.replace(/\++/g, '+');
      n = n.replace(/^\++/, '+');
      n = n.replace(/(?!^)\+/g, '');
      // If it doesn't start with +, add it
      if (!n.startsWith('+')) {
        n = `+${n.replace(/\+/g, '')}`;
      }
      // Remove initial zeros after the +
      n = n.replace(/^\+(0+)/, '+');
      return n;
    };

    const normalizedTo = normalize(to);
    if (window.FonemaWhatsappAudio?.call) {
      disableButtons();

      // Call started from UI => we treat it as outgoing
      await window.FonemaWhatsappAudio.call({
        elementId: 'incoming-call',
        toPhoneNumber: normalizedTo,
        html: '.'
      });
    }
    return true;
  }, [disableButtons, enabled]);

  if (!enabled) {
    return {
      answerCall: noop,
      declineCall: noop,
      endCall: noop,
      toggleMic: noopBool,
      call: noop,
      cancelCall: noop
    };
  }

  return {
    /**
     * Answer the incoming call.
     */
    answerCall,
    /**
     * Decline the incoming call.
     */
    declineCall,
    /**
     * End the active call.
     */
    endCall,
    /**
     * Toggle the microphone state (mute/unmute).
     * @returns {boolean} Nuevo estado de mute (true si está muteado, false si no)
     */
    toggleMic,
    /**
     * Start an outgoing call.
     */
    call,
    /**
     * Cancel the current outgoing call while it is ringing and clean up all references.
     */
    cancelCall
  };
}
